import React, { useState, useRef, useEffect } from 'react';
import OsScroll from './OsScroll';
import { ChevronDown, RefreshCw } from 'lucide-react';

/**
 * Reusable searchable single-select (combobox): one input that shows the
 * selected item, turns into a filter on click, opens a dropdown of options, and
 * keeps the selection when closed without picking a new one.
 *
 * Props:
 *  - options: any[]                          list of items
 *  - value: any                             selected key (compared via getKey)
 *  - onChange: (key, option) => void
 *  - getKey?: (option) => any               default: o.value
 *  - getLabel?: (option) => string          default: o.label
 *  - renderOption?: (option, { selected }) => node   custom row content
 *  - placeholder?: string                   shown when nothing selected (closed)
 *  - searchPlaceholder?: string             shown while searching
 *  - loading?: bool, error?: string, emptyText?: string
 *  - noMatchText?: string                    shown when a search matches nothing
 *  - onEmptyAction?: () => void              makes the empty-list row clickable (keeps open)
 *  - onOpen?: () => void                     fired when the dropdown opens (lazy load)
 *  - disabled?: bool
 *  - leadingIcon?: node                      icon inside the control
 */
export default function SearchableSelect({
  options = [],
  value,
  onChange,
  getKey = (o) => o.value,
  getLabel = (o) => o.label,
  renderOption,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  loading = false,
  error = '',
  emptyText = 'No results',
  noMatchText = 'No matches',
  onEmptyAction,
  onOpen,
  disabled = false,
  leadingIcon = null,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find((o) => getKey(o) === value) || null;
  const selectedLabel = selected ? getLabel(selected) : '';

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const openDropdown = () => {
    if (disabled || open) return;
    setOpen(true);
    setQuery('');
    onOpen?.();
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter((o) => getLabel(o).toLowerCase().includes(q))
    : options;

  const pick = (o) => {
    onChange?.(getKey(o), o);
    close();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      close();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length > 0) pick(filtered[0]);
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative', width: '100%' }}>
        {leadingIcon && (
          <div
            className="pointer-events-none"
            style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}
          >
            {leadingIcon}
          </div>
        )}
        <input
          ref={inputRef}
          type="text"
          className="modal-input"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            paddingLeft: leadingIcon ? '44px' : undefined,
            paddingRight: '34px',
            cursor: open ? 'text' : 'pointer',
          }}
          value={open ? query : selectedLabel}
          placeholder={open ? (selectedLabel || searchPlaceholder) : placeholder}
          readOnly={!open}
          disabled={disabled}
          onMouseDown={openDropdown}
          onFocus={openDropdown}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <ChevronDown
          size={16}
          className="pointer-events-none text-gray-400"
          style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}
        />
      </div>

      {open && (
        <div
          className="auth-picker__menu"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            zIndex: 10040,
          }}
        >
          {loading ? (
            <div className="auth-picker__row" style={{ cursor: 'default' }}>
              <RefreshCw size={14} className="animate-spin" />
              <span>Loading…</span>
            </div>
          ) : error ? (
            <div className="auth-picker__row" style={{ cursor: 'default', color: 'var(--danger, #ef4444)' }}>
              {error}
            </div>
          ) : options.length === 0 ? (
            onEmptyAction ? (
              <button
                type="button"
                className="auth-picker__row"
                style={{ width: '100%' }}
                onClick={onEmptyAction}
              >
                {emptyText}
              </button>
            ) : (
              <div className="auth-picker__row" style={{ cursor: 'default' }}>{emptyText}</div>
            )
          ) : filtered.length === 0 ? (
            <div className="auth-picker__row" style={{ cursor: 'default' }}>{noMatchText}</div>
          ) : (
            // The app's own scrollbar, not the browser's (owner, 04.10.2026: "Search
            // your repositories" showed the native one). OsScroll scrolls; the
            // list keeps its row padding but not its own overflow.
            <OsScroll style={{ maxHeight: '16rem' }}>
            <div className="auth-picker__list auth-picker__list--os">
              {filtered.map((o) => {
                const isSel = getKey(o) === value;
                return (
                  <button
                    key={String(getKey(o))}
                    type="button"
                    onClick={() => pick(o)}
                    className={`auth-picker__row ${isSel ? 'auth-picker__row--active' : ''}`}
                  >
                    {renderOption ? renderOption(o, { selected: isSel }) : (
                      <span className="min-w-0 flex-1 truncate">{getLabel(o)}</span>
                    )}
                  </button>
                );
              })}
            </div>
            </OsScroll>
          )}
        </div>
      )}
    </div>
  );
}
